/* eslint-disable react/jsx-no-target-blank */
import {useEffect} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useLocation} from 'react-router-dom'
import {AsideMenuItem} from './AsideMenuItem'
import {usePermissions} from '../../../../app/modules/auth'
import {WMS_PAGE_PERMISSION, WMS_SECTIONS, canOpenPage} from '../../../../app/wms/wmsPages'
import {useWmsMockStore} from '../../../../app/wms/mock/store'
import {navCount} from '../../../../app/wms/mock/selectors'
import {fetchClient} from '../../../../shared/api'
import {useDeliveryCounters} from '../../../../app/wms/delivery/api'

/**
 * Открытые задания по всем девяти этапам одним запросом (SCRUM-530/531, см.
 * wms-task-counters-api.md) — раньше каждый бейдж считался отдельным
 * GET /wms/tasks/?type=... (или вообще молчал через мёртвый mock-стор), из-за
 * чего QA поймала «большинство этапов без бейджа при наличии открытых
 * заданий» и «Задания на выдачу показывает устаревшую 1». Один агрегирующий
 * эндпоинт — единственный источник правды для всех девяти.
 *
 * Выдача двухфазная (SCRUM-546/548): одно и то же задание type=handoff
 * сначала у Контролера (`at_expedition`), потом у Менеджера (`at_handout`).
 * «Выдача» считает первую фазу, «Задания на выдачу» — вторую; `handoff`
 * остаётся их суммой и бейджем больше не показывается.
 */
interface TaskCounters {
  search: number
  movement: number
  acceptance: number
  placement: number
  internal_move: number
  picking: number
  pick_control: number
  expedition: number
  handoff: number
  handoff_at_expedition: number
  handoff_at_handout: number
  /** «Поступления от ТТЦ» — клиенты (строки страницы), а не задания; null,
   *  если у роли нет wms:page:ttc-receipts:view. */
  ttc_receipts: number | null
}

const STAGE_COUNTER_KEY: Partial<Record<string, keyof TaskCounters>> = {
  'tasks-search': 'search',
  'tasks-movement': 'movement',
  'tasks-acceptance': 'acceptance',
  'tasks-placement': 'placement',
  moves: 'internal_move',
  'tasks-picking': 'picking',
  'tasks-control': 'pick_control',
  'tasks-expedition': 'expedition',
  'tasks-handoff': 'handoff_at_handout',
  handoff: 'handoff_at_expedition',
  ttc: 'ttc_receipts',
}

type RawGet = (path: string) => Promise<{data?: unknown; error?: unknown}>

/** ~30с — периодичность, которую просит DoD; плюс рефетч при каждой смене
 *  маршрута, раз сайдбар не перемонтируется между страницами /wms/*. */
const useTaskCounters = () => {
  const location = useLocation()
  const query = useQuery({
    queryKey: ['wms', 'tasks', 'counters'],
    queryFn: async (): Promise<TaskCounters> => {
      const {data, error} = await (fetchClient.GET as unknown as RawGet)('/wms/tasks/counters/')
      if (error) throw error
      return data as TaskCounters
    },
    refetchInterval: 30_000,
  })
  const refetch = query.refetch
  useEffect(() => {
    refetch()
  }, [location.pathname, refetch])
  return query
}

/**
 * Пункты меню, сгруппированные по секциям нового task-based flow
 * (см. wms-prototype.html / wmsPages.ts). Каждый пункт — {key, icon, title}.
 * `key` ищется в WMS_PAGE_PERMISSION для проверки видимости и в navCount()
 * для счётчика-бейджа (см. wms-prototype.html · navCount()).
 */
const OPERATIONS_ITEMS = [
  {key: 'dashboard', icon: 'layout-grid', title: 'Дашборд', comingSoon: true},
  {key: 'orders', icon: 'layers-2', title: 'Все заказы'},
  {key: 'dispatch', icon: 'monitor', title: 'Рабочее место диспетчера', comingSoon: true},
  {key: 'ttc', icon: 'truck', title: 'Поступления от ТТЦ'},
]
const RECEIVING_ITEMS = [
  {key: 'tasks-search', icon: 'search', title: 'Поиск'},
  {key: 'tasks-movement', icon: 'shopping-cart', title: 'Перемещение'},
  {key: 'tasks-acceptance', icon: 'shield-check', title: 'Приёмка и контроль'},
  {key: 'tasks-placement', icon: 'lock', title: 'Размещение'},
]
const WAREHOUSE_ITEMS = [
  {key: 'storage', icon: 'grid', title: 'Хранение'},
  {key: 'moves', icon: 'swap', title: 'Внутренние перемещения'},
  {key: 'inventory', icon: 'clock', title: 'Инвентаризация', comingSoon: true},
]
const HANDOVER_ITEMS = [
  {key: 'requests', icon: 'file-text', title: 'Заявки клиентов'},
  {key: 'tasks-picking', icon: 'shopping-basket', title: 'Комплектация'},
  {key: 'tasks-control', icon: 'search', title: 'Контроль комплектации'},
  {key: 'tasks-expedition', icon: 'navigation', title: 'Экспедиция'},
  {key: 'tasks-handoff', icon: 'log-out', title: 'Задания на выдачу'},
  {key: 'handoff', icon: 'hand', title: 'Выдача'},
]
/** SCRUM-565: бейджи — GET /wms/delivery/counters/ (to_deliver / active_tasks). */
const DELIVERY_ITEMS = [
  {key: 'delivery', icon: 'package', title: 'К доставке'},
  {key: 'delivery-active', icon: 'truck', title: 'Активные доставки'},
  {key: 'kassa', icon: 'wallet', title: 'Касса'},
]
const DELIVERY_COUNTER_KEY: Record<string, 'to_deliver' | 'active_tasks'> = {
  delivery: 'to_deliver',
  'delivery-active': 'active_tasks',
}
const ANALYTICS_ITEMS = [
  {key: 'reports', icon: 'chart-column', title: 'Отчёты', comingSoon: true},
  {key: 'kpi', icon: 'award', title: 'KPI сотрудников', comingSoon: true},
]
const DIRECTORIES_ITEMS = [
  {key: 'vehicles', icon: 'car', title: 'Автомобили'},
  {key: 'geozones', icon: 'map', title: 'Геозоны'},
]
const ADMIN_ITEMS = [
  {key: 'staff', icon: 'users', title: 'Сотрудники'},
  {key: 'pda-devices', icon: 'smartphone', title: 'Устройства ТСД'},
  {key: 'settings', icon: 'settings', title: 'Настройки'},
  {key: 'norms', icon: 'timer', title: 'Нормативы'},
]

type NavItem = {key: string; title: string; icon?: string; comingSoon?: boolean}

const SectionLabel = ({children}: {children: string}) => (
  <div className='menu-item'>
    <div className='menu-content pt-4 pb-2'>
      <span className='menu-section text-muted text-uppercase fs-8 ls-1'>{children}</span>
    </div>
  </div>
)

export function AsideMenuMain() {
  const {permissions} = usePermissions()
  const mock = useWmsMockStore()
  const countersQuery = useTaskCounters()

  // Visibility helper: a page with an empty/undefined permission code is open to
  // any authenticated WMS user except a Кассир (same rule as WmsRoutes) — only
  // pages with a real backend-issued code need the permission itself.
  const can = (key: string): boolean =>
    canOpenPage(WMS_PAGE_PERMISSION[key] || undefined, permissions)

  const visibleDelivery = DELIVERY_ITEMS.filter((i) => can(i.key))
  // «Касса» без бейджа — счётчики нужны только «К доставке» / «Активным доставкам».
  const deliveryCounters = useDeliveryCounters(
    visibleDelivery.some((i) => i.key in DELIVERY_COUNTER_KEY)
  )

  const badgeFor = (key: string) => {
    const deliveryKey = DELIVERY_COUNTER_KEY[key]
    if (deliveryKey) {
      const n = deliveryCounters.data?.[deliveryKey] ?? 0
      if (!n) return undefined
      return <span className='badge badge-circle badge-warning fw-bold fs-8'>{n}</span>
    }
    const counterKey = STAGE_COUNTER_KEY[key]
    if (counterKey) {
      const n = countersQuery.data?.[counterKey] ?? 0
      if (!n) return undefined
      return <span className='badge badge-circle badge-warning fw-bold fs-8'>{n}</span>
    }
    const b = navCount(mock, key)
    if (!b) return undefined
    return <span className={`badge badge-circle badge-${b.tone} fw-bold fs-8`}>{b.count}</span>
  }

  const renderItems = (items: NavItem[]) =>
    items
      .filter((i) => can(i.key))
      .map((i) => (
        <AsideMenuItem
          key={i.key}
          to={`/wms/${i.key}`}
          icon={i.icon}
          title={i.title}
          disabled={i.comingSoon}
          badge={
            i.comingSoon ? <span className='badge badge-light fs-9'>Скоро</span> : badgeFor(i.key)
          }
        />
      ))

  const visibleOperations = OPERATIONS_ITEMS.filter((i) => can(i.key))
  const visibleReceiving = RECEIVING_ITEMS.filter((i) => can(i.key))
  const visibleWarehouse = WAREHOUSE_ITEMS.filter((i) => can(i.key))
  const visibleHandover = HANDOVER_ITEMS.filter((i) => can(i.key))
  const visibleAnalytics = ANALYTICS_ITEMS.filter((i) => can(i.key))
  const visibleDirectories = DIRECTORIES_ITEMS.filter((i) => can(i.key))
  const visibleAdmin = ADMIN_ITEMS.filter((i) => can(i.key))

  return (
    <>
      {visibleOperations.length > 0 && (
        <>
          <SectionLabel>{WMS_SECTIONS.operations}</SectionLabel>
          {renderItems(OPERATIONS_ITEMS)}
        </>
      )}

      {visibleReceiving.length > 0 && (
        <>
          <SectionLabel>{WMS_SECTIONS.receiving}</SectionLabel>
          {renderItems(RECEIVING_ITEMS)}
        </>
      )}

      {visibleWarehouse.length > 0 && (
        <>
          <SectionLabel>{WMS_SECTIONS.warehouse}</SectionLabel>
          {renderItems(WAREHOUSE_ITEMS)}
        </>
      )}

      {visibleHandover.length > 0 && (
        <>
          <SectionLabel>{WMS_SECTIONS.handover}</SectionLabel>
          {renderItems(HANDOVER_ITEMS)}
        </>
      )}

      {visibleDelivery.length > 0 && (
        <>
          <SectionLabel>{WMS_SECTIONS.delivery}</SectionLabel>
          {renderItems(DELIVERY_ITEMS)}
        </>
      )}

      {visibleAnalytics.length > 0 && (
        <>
          <SectionLabel>{WMS_SECTIONS.analytics}</SectionLabel>
          {renderItems(ANALYTICS_ITEMS)}
        </>
      )}

      {visibleDirectories.length > 0 && (
        <>
          <SectionLabel>{WMS_SECTIONS.directories}</SectionLabel>
          {renderItems(DIRECTORIES_ITEMS)}
        </>
      )}

      {visibleAdmin.length > 0 && (
        <>
          <SectionLabel>{WMS_SECTIONS.admin}</SectionLabel>
          {renderItems(ADMIN_ITEMS)}
        </>
      )}
    </>
  )
}
